import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { KeyRound, Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AccessEntryCodesEditor
 * ----------------------
 * Property-Manager scoped editor to add / rotate gate + building entry codes
 * live. Saves via PUT /api/properties/{property_id}/structure so field
 * agents see the new codes on their next dashboard refresh.
 */
export function AccessEntryCodesEditor({ propertyId, propertyName }) {
  const [gateCodes, setGateCodes] = useState([]);
  const [newGate, setNewGate] = useState('');
  const [buildings, setBuildings] = useState([]);     // [{building_number, ...}]
  const [buildingCodes, setBuildingCodes] = useState({}); // { bn: code }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/structure`, { credentials: 'include' });
        if (res.ok) {
          const s = await res.json();
          setGateCodes(s.gate_entry_codes || []);
          setBuildingCodes(s.building_entry_codes || {});
          setBuildings(s.buildings || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [propertyId]);

  const addGate = () => {
    const v = (newGate || '').trim();
    if (!v) return;
    setGateCodes((g) => [...g, v]);
    setNewGate('');
  };
  const removeGate = (i) => setGateCodes((g) => g.filter((_, j) => j !== i));

  const setBldgCode = (bn, val) => setBuildingCodes((m) => ({ ...m, [String(bn)]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gate_entry_codes: gateCodes,
        building_entry_codes: Object.fromEntries(
          Object.entries(buildingCodes).filter(([, v]) => (v || '').trim() !== ''),
        ),
      };
      const res = await fetch(`${BACKEND_URL}/api/properties/${propertyId}/structure`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      toast.success('Access codes updated. Field agents will see them on next refresh.');
    } catch (e) {
      toast.error('Could not save codes. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!propertyId) return null;

  return (
    <Card
      className="bg-white border-2 p-4 sm:p-6"
      style={{ borderColor: 'rgba(201, 169, 98, 0.4)' }}
      data-testid="access-entry-codes-editor"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-zinc-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <KeyRound className="h-5 w-5 mr-2" style={{ color: '#C9A962' }} />
          ACCESS &amp; ENTRY CODES {propertyName ? `· ${propertyName}` : ''}
        </h3>
        <Button
          onClick={save}
          disabled={saving || loading}
          className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
          data-testid="codes-save"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : 'Save codes'}
        </Button>
      </div>

      <p className="text-xs text-zinc-600 mb-4">
        Update the gate + building codes here whenever you rotate them. The next agent that opens their job card will see the new codes automatically.
      </p>

      {/* Gate codes */}
      <div className="mb-4">
        <Label className="text-xs uppercase tracking-wider text-zinc-600">Gate / perimeter codes</Label>
        <div className="flex flex-wrap gap-2 mt-2 mb-2">
          {gateCodes.length === 0 && (
            <p className="text-xs text-zinc-500">No gate codes on file.</p>
          )}
          {gateCodes.map((c, i) => (
            <Badge
              key={`${c}-${i}`}
              className="bg-[#C9A962]/20 border border-[#C9A962]/50 text-[#8a6f3f] font-mono flex items-center gap-1"
              data-testid={`codes-gate-${i}`}
            >
              {c}
              <button
                type="button"
                onClick={() => removeGate(i)}
                className="ml-1 hover:text-red-600"
                aria-label={`Remove code ${c}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="New gate code (e.g. 1234#)"
            value={newGate}
            onChange={(e) => setNewGate(e.target.value)}
            className="flex-1"
            data-testid="codes-new-gate-input"
          />
          <Button onClick={addGate} variant="outline" size="sm" data-testid="codes-add-gate">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Building codes */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-600">Per-building entry codes</Label>
        {buildings.length === 0 ? (
          <p className="text-xs text-zinc-500 mt-2">
            No buildings on file yet. Add them from the Property Structure editor first.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {buildings.map((b) => (
              <div
                key={b.building_number}
                className="flex items-center gap-2 p-2 rounded-md border border-zinc-200 bg-zinc-50"
                data-testid={`codes-building-row-${b.building_number}`}
              >
                <Badge className="bg-[#FFD60A] text-black text-[10px]">
                  Bldg {b.building_number}
                </Badge>
                <Input
                  value={buildingCodes[String(b.building_number)] || ''}
                  onChange={(e) => setBldgCode(b.building_number, e.target.value)}
                  placeholder="Code (leave blank for none)"
                  className="flex-1 h-8 text-sm font-mono"
                  data-testid={`codes-building-input-${b.building_number}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default AccessEntryCodesEditor;
